// Client wrappers for admin callables. All calls fail with
// permission-denied for non-admin users — the UI must still gate access
// to avoid an obvious failed call.

import { getFunctions, httpsCallable } from 'firebase/functions';
import { app } from './firebase';

function functions() {
  return getFunctions(app);
}

export interface GrantRecord {
  userId: string;
  email: string | null;
  displayName: string | null;
  tier: string;
  coachAddOn: boolean;
  grantedBy: string;
  grantedAt: string;
  expiresAt: string | null;
  note: string | null;
}

export async function grantAccess(input: {
  userId: string;
  tier: 'learner' | 'pro' | 'teams';
  coachAddOn: boolean;
  durationDays: number | null;
  note?: string | null;
  // Teams tier only: also provision a real team for the user.
  createTeamForUser?: boolean;
  teamName?: string | null;
  teamSeats?: number | null; // total licenses incl. founder
  coachSeats?: number | null;
}): Promise<{ ok: true; tier: string; expiresAt: string | null; teamId: string | null }> {
  const fn = httpsCallable(functions(), 'grantAccess');
  const { data } = await fn(input);
  return data as { ok: true; tier: string; expiresAt: string | null; teamId: string | null };
}

export async function revokeGrant(userId: string): Promise<{ ok: true }> {
  const fn = httpsCallable(functions(), 'revokeGrant');
  const { data } = await fn({ userId });
  return data as { ok: true };
}

export async function listGrants(): Promise<{ grants: GrantRecord[] }> {
  const fn = httpsCallable(functions(), 'listGrants');
  const { data } = await fn({});
  return data as { grants: GrantRecord[] };
}

export interface DashboardStats {
  totalUsers: number;
  tierBreakdown: { free: number; learner: number; pro: number; teams: number };
  activeGrants: number;
  recentSignups: Array<{
    uid: string;
    email: string | null;
    displayName: string | null;
    tier: string;
    createdAt: number;
  }>;
}

export async function getAdminDashboardStats(): Promise<DashboardStats> {
  const fn = httpsCallable(functions(), 'getAdminDashboardStats');
  const { data } = await fn({});
  return data as DashboardStats;
}

export interface AdminUserRecord {
  uid: string;
  email: string | null;
  displayName: string | null;
  tier: string;
  role: string | null;
  /** Epoch ms, or null when the user document has no usable creation date. */
  createdAt: number | null;
  lastActiveAt: number | null;
  hasGrant: boolean;
  grantExpiresAt: string | null;
  coachAddOn: boolean;
}

export async function adminListUsers(): Promise<{ users: AdminUserRecord[] }> {
  const fn = httpsCallable(functions(), 'adminListUsers');
  const { data } = await fn({});
  return data as { users: AdminUserRecord[] };
}

export interface ClaudeMetricsBucket {
  key: string;
  calls: number;
  costUsd: number;
  cacheReadTokens: number;
  totalInputTokens: number;
  outputTokens: number;
  cacheHitRate: number;
}

export interface ClaudeMetricsSummary {
  windowDays: number;
  generatedAt: string;
  truncated: boolean;
  overall: {
    calls: number;
    costUsd: number;
    cacheReadTokens: number;
    totalInputTokens: number;
    outputTokens: number;
    cacheHitRate: number;
    verbatimHits: number;
  };
  byTag: ClaudeMetricsBucket[];
  byModel: ClaudeMetricsBucket[];
  byDay: ClaudeMetricsBucket[];
}

export async function getClaudeMetricsSummary(
  windowDays = 7,
): Promise<ClaudeMetricsSummary> {
  const fn = httpsCallable(functions(), 'getClaudeMetricsSummary');
  const { data } = await fn({ windowDays });
  return data as ClaudeMetricsSummary;
}

export interface WaitlistSignupRecord {
  id: string;
  email: string;
  source: string | null;
  createdAt: number | null;
  userAgent: string | null;
  referrer: string | null;
}

export async function adminListWaitlistSignups(): Promise<{ signups: WaitlistSignupRecord[] }> {
  const fn = httpsCallable(functions(), 'adminListWaitlistSignups');
  const { data } = await fn({});
  return data as { signups: WaitlistSignupRecord[] };
}

// ─── Service health (external-service configuration) ───
// Verdicts only — no callable in this group ever returns a secret VALUE.

export type SecretState = 'configured' | 'placeholder' | 'missing';
export type ServiceState = 'operational' | 'degraded' | 'down';

export interface ServiceSecretStatus {
  name: string;
  state: SecretState;
}

export interface ServiceStatus {
  id: string;
  label: string;
  impact: string;
  blocksLaunch: boolean;
  state: ServiceState;
  secrets: ServiceSecretStatus[];
  fixCommand: string;
}

export interface ServiceIncidentRecord {
  serviceId: string;
  lastReason: string;
  lastContext: string;
  lastFailureAt: number;
  firstFailureAt: number;
  failureCount: number;
}

export interface ServiceHealthReport {
  state: ServiceState;
  services: ServiceStatus[];
  blocking: string[];
  generatedAt: string;
  incidents: ServiceIncidentRecord[];
}

export async function getServiceHealth(): Promise<ServiceHealthReport> {
  const fn = httpsCallable(functions(), 'getServiceHealth');
  const { data } = await fn({});
  return data as ServiceHealthReport;
}

export async function clearServiceIncidentRecord(serviceId: string): Promise<{ ok: true }> {
  const fn = httpsCallable(functions(), 'clearServiceIncidentRecord');
  const { data } = await fn({ serviceId });
  return data as { ok: true };
}
